<template>
  <transition name="fade">
    <div class="modal-mask">
      <div class="modal-wrapper">
        <focus-trap
          v-model="modalOptions.active"
          :initial-focus="() => $refs.input"
        >
          <div
            class="modal-container"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-label"
          >
            <div id="modal-label" class="sr-only">
              {{ $t("modal_label_WCAG") }}
            </div>
            <div class="modal-header">
              <slot name="header">
                <div class="default-header f-clear">
                  <ul
                    class="list-inline f-left"
                    v-if="availableCultures.length < maxTabs"
                  >
                    <li v-for="(item, i) in availableCultures" :key="i">
                      <a
                        role="button"
                        href="#"
                        @click.prevent="setActive(item.culture)"
                        :class="{ active: isTabActive(item.culture) }"
                      >
                        <div>{{ item.name }}</div>
                        <div
                          class="default"
                          v-if="defaultCulture === item.culture"
                        >
                          ({{ $t("default") }})
                        </div>
                      </a>
                    </li>
                  </ul>
                  <div class="modal-dropdown-container f-left" v-else>
                    <cert-dropdown
                      :id="'modal-culture-dropdown'"
                      :dropdown-values="dropdownData"
                      :dropdown-default-text="dropdownDefault"
                      @change="changeCulture"
                    ></cert-dropdown>
                  </div>
                  <a href="#" class="f-right close" @click.prevent="closeModal">
                    {{ $t("close") }}
                    <div>
                      <close-icon aria-hidden="true"></close-icon>
                    </div>
                  </a>
                </div>
              </slot>
            </div>

            <div class="modal-body">
              <slot name="body">
                <div class="default-body">
                  <div class="input-title">
                    {{ $t("certificate_" + modalOptions.editedValue) }}
                  </div>
                  <div class="input-container">
                    <input
                      type="text"
                      ref="input"
                      :maxlength="modalOptions.inputLength"
                      :value="inputValue"
                      @input="updateValue"
                    />
                    <a href="#" class="clear" @click.prevent="clearInput">
                      <clear-icon aria-hidden="true" class="w-17"></clear-icon>
                    </a>
                  </div>
                </div>
              </slot>
            </div>

            <div class="modal-footer">
              <slot name="footer">
                <div class="default-footer">
                  <cert-confirm-button
                    :button-text="$t('save_modal')"
                    @click="saveAndCloseModal"
                  ></cert-confirm-button>
                </div>
              </slot>
            </div>
          </div>
        </focus-trap>
      </div>
    </div>
  </transition>
</template>

<script>
import { FocusTrap } from "focus-trap-vue";
import CertDropdown from "../form/CertDropdown";
import CertConfirmButton from "../form/CertConfirmButton";
import CloseIcon from "@/assets/icon-close.svg";
import ClearIcon from "@/assets/icon-clear.svg";

export default {
  name: "CertModal",
  props: {
    modalOptions: Object,
    modalData: Object,
    defaultCulture: String,
    availableCultures: Array,
  },
  data() {
    return {
      maxTabs: 7,
      activeTab: this.defaultCulture,
      inputNewValue: {},
      cleared: false,
    };
  },
  components: {
    FocusTrap,
    CertDropdown,
    CertConfirmButton,
    CloseIcon,
    ClearIcon,
  },
  methods: {
    closeModal() {
      this.modalOptions.active = false;
      this.cleared = false;
      if (this.modalOptions.focusBackElem instanceof HTMLElement) {
        this.modalOptions.focusBackElem.focus();
      }
    },
    isTabActive(culture) {
      return this.activeTab === culture;
    },
    setActive(culture) {
      this.activeTab = culture;
      this.cleared = false;
    },
    changeCulture(cultureObj) {
      this.activeTab = cultureObj.value;
      this.cleared = false;
    },
    updateValue(e) {
      this.inputNewValue[this.activeTab] = e.target.value;
    },
    clearInput() {
      this.inputNewValue[this.activeTab] = "";
      this.cleared = true;
    },
    saveAndCloseModal() {
      this.availableCultures.forEach((el) => {
        this.modalData.certificate_template.resources[el.culture][
          this.modalOptions.editedValue
        ] = this.inputNewValue[el.culture];
      });
      this.closeModal();
    },
  },
  computed: {
    inputValue() {
      return this.inputNewValue[this.activeTab];
    },
    dropdownData() {
      let arr = [];
      this.availableCultures.forEach((x) =>
        arr.push({
          value: x.culture,
          text:
            x.name +
            (x.culture === this.defaultCulture
              ? " (" + this.$t("default") + ")"
              : ""),
        })
      );
      return arr;
    },
    dropdownDefault() {
      for (let i = 0; i < this.availableCultures.length; ++i) {
        if (this.availableCultures[i].culture === this.defaultCulture) {
          return (
            this.availableCultures[i].name + " (" + this.$t("default") + ")"
          );
        }
      }

      return "";
    },
  },
  created() {
    let obj = {};
    this.availableCultures.forEach((el) => {
      obj[el.culture] = this.modalData.certificate_template.resources[
        el.culture
      ][this.modalOptions.editedValue];
    });
    this.inputNewValue = Object.assign({}, this.inputNewValue, obj);
  },
};
</script>

<style lang="scss" scoped>
.mtm {
  .modal-container .default-header {
    ul {
      > li {
        a.active {
          border-bottom-color: $exMtm;
          color: $exMtm;
        }
        a:focus,
        a:active {
          color: $exMtmActive;
        }
      }
    }
    .close {
      &:hover,
      &:focus {
        color: $exMtm;

        > div {
          border-color: $exMtm;
        }
      }
    }
  }
  .modal-container .default-body {
    .input-container {
      > a > svg:hover,
      svg:focus {
        filter: invert(34%) sepia(79%) saturate(423%) hue-rotate(124deg)
          brightness(99%) contrast(99%);
      }
    }
  }
}
.w-17 {
  width: 17px;
}

.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: table;
  transition: opacity 0.3s ease;
}

.modal-wrapper {
  //   display: table-cell;
  //   vertical-align: middle;
  margin-top: 45px;
}

.modal-container {
  width: 930px;
  margin: 0px auto;
  background-color: #fff;
  border-radius: $borderRadius;
  box-shadow: $dropShadow;
  transition: all 0.3s ease;

  .default-header {
    padding: 32px 32px 0 0;

    .close {
      color: $exBlack;
      font-weight: bold;
      @include fontSize(11px);
      position: relative;
      padding-right: 45px;
      > div {
        height: 31px;
        width: 31px;
        border: 1px solid $borderLightGray;
        border-radius: 50%;
        display: inline-block;
        position: absolute;
        top: -8px;
        right: 0;
        padding: 9px;
      }

      &:hover,
      &:focus {
        color: $exBlue;

        > div {
          border-color: $exBlue;

          > img {
            filter: invert(19%) sepia(93%) saturate(2569%) hue-rotate(208deg)
              brightness(92%) contrast(102%);
          }
        }
      }
    }

    .modal-dropdown-container {
      margin-left: 30px;
      width: 170px;
      padding-bottom: 32px;
    }

    ul {
      > li {
        width: 110px;
        height: 50px;
        vertical-align: bottom;
        a {
          display: block;
          height: 100%;
          width: 100%;
          text-decoration: none;
          color: $exBlack;
          font-weight: bold;
          text-align: center;
          @include fontSize(14px);
          background-color: #fff;
          border: 0;
          border-bottom: 3px solid transparent;
          cursor: pointer;

          .default {
            font-weight: normal;
            @include fontSize(12px);
          }
        }

        a.active {
          border-bottom-color: $exBlue;
          color: $exBlue;
        }

        a:focus,
        a:active {
          color: $exBlueActive;
        }
      }
    }
  }

  .default-body {
    background-color: $bgMediumGray;
    border-top: 1px solid $borderLightGray;
    border-bottom: 1px solid $borderLightGray;
    padding: 24px 32px;

    .input-title {
      color: $exBlack;
      @include fontSize(14px);
      font-weight: bold;
      line-height: 17px;
      margin-bottom: 8px;
    }

    input[type="text"] {
      width: 100%;
      height: 50px;
      border: 1px solid $borderLightGray;
      color: $exBlack;
      border-radius: $borderRadius;
      color: $exBlack;
      padding: 16px 48px 16px 16px;
    }

    .input-container {
      position: relative;

      > a {
        position: absolute;
        top: 14px;
        right: 16px;
        text-decoration: none;
        color: $exBlack;

        > svg {
          vertical-align: middle;
        }

        > svg:hover,
        svg:focus {
          filter: invert(19%) sepia(93%) saturate(2569%) hue-rotate(208deg)
            brightness(92%) contrast(102%);
        }
      }
    }
  }

  .default-footer {
    padding: 16px 32px;
  }
}
</style>
